import { IconCircleCheck } from "@tabler/icons-react";
import React from "react";
import { toast } from "sonner";

import { Loader } from "@/components/Loader";
import { useTeam } from "@/components/providers/team";
import { useFetchUserInvites } from "@/components/queries/user";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

export function AcceptTeamDialog({ inviteId }: { inviteId: string }) {
	const [isLoading, setIsLoading] = React.useState(false);
	const { refresh } = useTeam();
	const { refetchInvites } = useFetchUserInvites();
	return (
		<AlertDialog>
			<AlertDialogTrigger
				className={buttonVariants({
					variant: "success",
					size: "icon-sm",
				})}
				aria-label="Accept team invite"
			>
				<IconCircleCheck className="h-4 w-4" />
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Accept Team</AlertDialogTitle>
					<AlertDialogDescription>
						Are you sure you want to accept this team? This action cannot be
						undone.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction
						onClick={async () => {
							setIsLoading(true);
							try {
								await requestWithHandling<void>(() =>
									apiClient.api.userTeamsInviteCreate({ id: inviteId }),
								);
								toast.success("Team accepted successfully");
								await refresh();
								await refetchInvites();
							} catch (error) {
								toast.error("Failed to accept team", {
									description:
										error instanceof TypedClientApiError
											? error.message
											: "An unknown error occurred.",
								});
							} finally {
								setIsLoading(false);
							}
						}}
						disabled={isLoading}
						variant="success"
						className=""
					>
						{isLoading ? <Loader /> : "Accept"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
